// One PO: header (status, revision, send, PDF), terms and ship-to, the lines as a spreadsheet, and
// totals. Header fields save one at a time like any side panel; lines edit like Sell items' edit mode.
// A banner offers "Send update" when a line's spec has a newer released revision (Documents tab: PoDocuments).
// Packaging --- packaging lines sit in their own band with their print files; sending warns about missing ones, and a
// missing one's link opens the part's spec beside the PO to fix them (in the side pane).
// Product labels --- usePoLabels() once for the Lines and Documents tabs; sending warns about what's off on the labels.
import { FileText, History, ListPlus, Merge, RefreshCw, Send, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { coverDay } from "../../../../shared/cover.ts"; // Cover through ---
import type { Po, PoLine, Vendor } from "../../../../shared/db.gen.ts";
import { planToday } from "../../../../shared/planning.ts";
import {
  hasUnsentChanges,
  headerEditable,
  linesEditable,
  NEXT_STATUSES,
  PO_KIND_LABELS,
  PO_KINDS,
  PO_STATUS_LABELS,
  type PoStatus,
  poDates,
  poTotals,
} from "../../../../shared/po.ts";
import { type DraftCheck, needsWarning } from "../../../../shared/po-plan-check.ts";
import { shortDate } from "../../lib/costs.ts";
import { ago, money, personName, plural, qty } from "../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../lib/mutate.ts";
import { checkNow, PlanCheckRunner, usePlanCheck } from "../../lib/planning.ts"; // Check against the plan ---
import { navigate, setSearch, useSearch } from "../../lib/router.tsx";
import { me } from "../../lib/session.ts";
import { useRow, useRows } from "../../lib/sync.ts";
import { Badge, Button, cx, Select } from "../../ui/controls.tsx";
import { Popover } from "../../ui/Popover.tsx";
import { PrintMenu } from "../../ui/PrintMenu.tsx";
import { type FieldDef, Fields } from "../../ui/RecordPanel.tsx";
import { PaneLink, useInPane } from "../../ui/SidePane.tsx";
import { toast } from "../../ui/toast.tsx";
import { MakeOrderView } from "../consignment/MakeOrderView.tsx"; // Consignment ---
import { type LineSpec, useLineSpecs } from "./docs.ts";
import { daysLate, poPath, STATUS_TONE, useLinesByPo, useVendorTerms, vendorLabel } from "./lib.ts";
import { PasteLinesButton, PasteLinesDialog } from "./PasteLines.tsx";
import { PoBills } from "./PoBills.tsx"; // Bills and payments ---
import { PoDocuments } from "./PoDocuments.tsx";
import { AcknowledgeDialog, PoEmailStrip, SendToVendorButton } from "./PoEmail.tsx"; // PO email ---
import { PoLandedCost } from "./PoLandedCost.tsx"; // Landed cost ---
import { AddItemSearch, PoLines } from "./PoLines.tsx";
import { PoPlanCheck, SendConfirm, sendAnyway } from "./PoPlanCheck.tsx"; // Check against the plan ---
import { IncomingBadge, PoReceiving } from "./PoReceiving.tsx";
import { PoUpdates, useUnseenUpdates } from "./PoUpdates.tsx"; // Order updates ---
import { type LabelGap, type PrintGap, usePoLabels, usePoPacking } from "./packaging.ts"; // Packaging ---
import { ShipTo } from "./ShipTo.tsx";
import { poLabels, ShippedDialog, TrackingText } from "./Tracking.tsx"; // Inbound tracking ---
import { VendorItemsDialog } from "./VendorItemsDialog.tsx";

type PoTab = "order" | "make" | "check" | "updates" | "documents" | "receipts" | "bills" | "costs" | "history";
const pickedTab = new Map<string, PoTab>();

export function PoPanel({ id, close }: { id: string; close: () => void }) {
  const inPane = useInPane(); // Side pane ---
  const po = useRow("po", id);
  const vendor = useRow("vendor", po?.vendor_id);
  const linesByPo = useLinesByPo();
  const lines = useMemo(() => linesByPo.get(id) ?? [], [linesByPo, id]);
  const terms = useVendorTerms(po?.vendor_id);
  // Consignment --- make POs open on their Make tab (builds at the CM); others never show it
  // Side pane --- the tab picked last for this PO, so back from a record opened from it (a print file's spec) lands there
  const [picked, pick] = useState<PoTab | null>(() => pickedTab.get(id) ?? null);
  const setTab = (t: PoTab) => {
    pickedTab.set(id, t);
    pick(t);
  };
  // Check against the plan --- drafts only (?tab=check opens on it). <PlanCheckRunner> works it out in idle moments, so a
  // sent PO never plans and an open draft never plans while rendering; a send works it out at once if it's behind.
  const checkable = po?.status === "draft" && po.kind !== "service";
  const { check, stale } = usePlanCheck(po, lines);
  const getCheck = () => (po && checkable ? (check && !stale ? check : checkNow(po, lines)) : null);
  const params = useSearch();
  const askedTab = params.get("tab");
  const asked = askedTab === "check" ? "check" : askedTab === "updates" ? "updates" : null; // Order updates --- ?tab=updates
  // ?tab=receipts (a receipt found with Ctrl+K) opens its receipts once and leaves the address, so the next PO opened
  // starts on its usual tab.
  useEffect(() => {
    if (askedTab !== "receipts") return;
    pick("receipts");
    setSearch({ tab: null });
  }, [askedTab]);
  const want = picked ?? asked;
  const tab =
    (want === "make" && po?.kind !== "make") || (want === "check" && !checkable) || (want === "updates" && po?.sent_revision == null)
      ? "order"
      : (want ?? (po?.kind === "make" ? "make" : "order"));
  const [picking, setPicking] = useState(false);
  const [viewsSlot, setViewsSlot] = useState<HTMLDivElement | null>(null); // Table views --- the lines table's menus
  const [pasting, setPasting] = useState(false); // Paste lines ---
  const specs = useLineSpecs(po, lines);
  const packing = usePoPacking(po, lines, specs); // Packaging ---
  const labels = usePoLabels(po, lines); // Product labels ---
  const unseen = useUnseenUpdates().get(id) ?? 0; // Order updates ---
  if (!po) return null;
  const sent = po.sent_revision != null; // Order updates --- once the vendor has it

  const headerOk = can("po.update") && headerEditable(po.status);
  const linesOk = can("po_line.update") && linesEditable(po.status);
  const totals = poTotals(lines, po.deposit_pct);

  return (
    // The lines need ~1000px: on smaller screens the PO covers the list (Esc goes back to it). In the side pane, the pane
    // sizes it (it floats over the list when there isn't room beside it).
    <aside
      className={cx(
        "flex h-full flex-col bg-surface",
        inPane ? "w-full" : "relative z-10 w-full shrink-0 border-l border-line min-[1600px]:w-[min(1120px,calc(100%-380px))]",
      )}
    >
      <Header
        po={po}
        vendor={vendor}
        vendorName={vendor?.name ?? ""}
        vendorShort={vendorLabel(vendor)}
        lineCount={lines.length}
        direct={lines.reduce((t, l) => t + Number(l.qty_direct ?? 0), 0)}
        close={close}
        check={check}
        getCheck={getCheck}
        review={() => setTab("check")}
        gaps={packing.gaps}
        labelGaps={labels.gaps}
      />
      {checkable && <PlanCheckRunner po={po} lines={lines} />}
      <PoEmailStrip po={po} vendor={vendor} check={check} getCheck={getCheck} review={() => setTab("check")} gaps={packing.gaps} labelGaps={labels.gaps} />
      <NewerSpecs po={po} lines={lines} specs={specs} />
      <div className="flex gap-4 border-b border-line px-4 text-[12px]">
        {(
          [
            ...(po.kind === "make" ? (["make"] as const) : []),
            "order",
            ...(checkable ? (["check"] as const) : []),
            ...(sent ? (["updates"] as const) : []), // Order updates ---
            "documents",
            "receipts",
            "bills", // Bills and payments --- deposit, balance, the bills and the 3-way match
            "costs",
            "history",
          ] as const
        ).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cx(
              "-mb-px flex items-center gap-1.5 border-b-2 py-2",
              t !== "check" && "capitalize",
              tab === t ? "border-accent text-text" : "border-transparent text-muted hover:text-text",
            )}
          >
            {t === "check" ? "Check against the plan" : t}
            {t === "check" && check && needsWarning(check, po.plan_check_key) && <Badge tone="warn">{check.differences + check.missing}</Badge>}
            {t === "updates" && unseen > 0 && (
              <span className="normal-case">
                <Badge tone="accent">{unseen} new</Badge>
              </span>
            )}
          </button>
        ))}
      </div>
      {tab === "make" ? (
        <MakeOrderView po={po} lines={lines} /> // Consignment ---
      ) : tab === "order" ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="max-h-[42%] shrink-0 overflow-y-auto border-b border-line p-2">
            <Terms po={po} disabled={!headerOk} />
          </div>
          <div className="flex shrink-0 items-center gap-2 border-b border-line px-3 py-2">
            {linesOk && po.kind === "make" ? (
              // Consignment --- a make PO's lines are builds: added on the Make tab with their BOM snapshot
              <span className="text-muted">
                Add builds on the{" "}
                <button type="button" className="text-accent-text hover:underline" onClick={() => setTab("make")}>
                  Make tab
                </button>
                , so each takes its BOM. Quantities and prices change here.
              </span>
            ) : linesOk ? (
              <>
                <AddItemSearch po={po} lines={lines} terms={terms} />
                <Button size="sm" variant="ghost" icon={<ListPlus className="size-3.5" />} onClick={() => setPicking(true)}>
                  Add {vendorLabel(vendor)}'s items
                </Button>
                <PasteLinesButton onClick={() => setPasting(true)} />
              </>
            ) : (
              <span className="text-muted">
                {PO_STATUS_LABELS[po.status]}: lines can't change{po.status === "cancelled" ? " (reopen it to edit)" : ""}.
              </span>
            )}
            <div ref={setViewsSlot} className="ml-auto flex shrink-0 items-center gap-1" />
          </div>
          <div className="min-h-0 flex-1">
            <PoLines
              po={po}
              lines={lines}
              terms={terms}
              editable={linesOk}
              specs={specs}
              packing={packing.byLine}
              labels={labels.byLine}
              viewsSlot={viewsSlot}
            />
          </div>
          <div className="flex shrink-0 items-center gap-4 border-t border-line bg-surface-2/50 px-4 py-2 text-[13px]">
            <span className="text-muted">
              {totals.lines} {totals.lines === 1 ? "line" : "lines"} · {qty(totals.qty)} units
            </span>
            {totals.unpriced > 0 && <Badge tone="warn">{totals.unpriced} without a price</Badge>}
            <span className="ml-auto text-muted">Subtotal</span>
            <span className="font-mono">{money(totals.subtotal, po.currency)}</span>
            {po.deposit_pct > 0 && (
              <>
                <span className="text-muted">Deposit {po.deposit_pct}%</span>
                <span className="font-mono">{money(totals.deposit, po.currency)}</span>
                <span className="text-muted">Balance</span>
                <span className="font-mono">{money(totals.balance, po.currency)}</span>
              </>
            )}
            <span className="font-semibold">Total {money(totals.subtotal, po.currency)}</span>
          </div>
        </div>
      ) : tab === "check" ? (
        check ? (
          <PoPlanCheck po={po} lines={lines} check={check} stale={stale} terms={terms} /> // Check against the plan ---
        ) : (
          <div className="p-6 text-center text-faint">Comparing with the plan…</div>
        )
      ) : tab === "updates" ? (
        <PoUpdates po={po} lines={lines} /> // Order updates ---
      ) : tab === "documents" ? (
        <PoDocuments po={po} lines={lines} specs={specs} vendor={vendor} packing={packing.byLine} partPacking={packing.byPart} labels={labels.byLine} />
      ) : tab === "receipts" ? (
        <PoReceiving po={po} />
      ) : tab === "bills" ? (
        <PoBills po={po} />
      ) : tab === "costs" ? (
        <PoLandedCost po={po} />
      ) : (
        <PoHistory id={po.id} />
      )}
      {picking && vendor && <VendorItemsDialog vendor={vendor} po={po} lines={lines} onClose={() => setPicking(false)} />}
      {pasting && vendor && <PasteLinesDialog vendor={vendor} po={po} lines={lines} onClose={() => setPasting(false)} />}
    </aside>
  );
}

function Header({
  po,
  vendor,
  vendorName,
  vendorShort,
  lineCount,
  direct,
  close,
  check,
  getCheck,
  review,
  gaps,
  labelGaps,
}: {
  po: Po;
  vendor: Vendor | undefined;
  vendorName: string;
  vendorShort: string;
  lineCount: number;
  /** Pre-sales --- units on its lines for backers. */
  direct: number;
  close: () => void;
  check: DraftCheck | null;
  getCheck: () => DraftCheck | null;
  review: () => void;
  /** Packaging --- packaging parts whose revision lacks print files. */
  gaps: readonly PrintGap[];
  /** Product labels --- products whose label has something off (usePoLabels()). */
  labelGaps: readonly LabelGap[];
}) {
  const inPane = useInPane(); // Side pane --- the pane has the close button
  const users = useRows("app_user");
  const [acking, setAcking] = useState(false); // PO email --- "Acknowledged" records what the vendor confirmed
  const [shipping, setShipping] = useState(false); // Inbound tracking --- "Shipped" asks how it travels
  // Check against the plan --- the warning before Mark as sent: the plan's differences (null: none), and Packaging --- the
  // print-file gaps, and Product labels --- what's off on the labels, read live while it's open.
  const [confirming, setConfirming] = useState<{ check: DraftCheck | null } | null>(null);
  // Packaging --- with no plan differences it hides once the gaps are fixed: it's closed then, so it can't come back by itself.
  const confirmShown = !!confirming && (!!confirming.check || gaps.length > 0 || labelGaps.length > 0);
  useEffect(() => {
    if (confirming && !confirmShown) setConfirming(null);
  }, [confirming, confirmShown]);
  const gmailOn = !!me()?.gmail?.enabled;
  const who = (id: string | null) => personName(users, id);
  const unsent = hasUnsentChanges(po);
  const canSend = can("po.mark_sent") && headerEditable(po.status) && (po.status === "draft" || unsent);
  const next = NEXT_STATUSES[po.status].filter((s) => !(po.status === "draft" && s === "sent"));

  // Check against the plan --- real differences staff haven't already gone through: warn first (Review / anyway).
  const markSent = (planCheck?: { key: string; note: string }) =>
    mutateWithResult("po.mark_sent", { id: po.id, ...(planCheck ? { plan_check: planCheck } : {}) }, [
      {
        t: "po",
        id: po.id,
        row: {
          ...po,
          status: po.status === "draft" ? "sent" : po.status,
          sent_revision: po.revision,
          sent_at: new Date().toISOString(),
          ...(planCheck ? { plan_check_overridden: true, plan_check_note: planCheck.note, plan_check_key: planCheck.key } : {}),
        },
      },
    ]);
  const move = (status: PoStatus) => {
    if (status === "acknowledged" && po.status === "sent" && can("po.acknowledge")) return setAcking(true);
    if (status === "shipped") return setShipping(true);
    // Pre-sales --- cancelling a make PO hands its units for backers back to planning.
    if (
      status === "cancelled" &&
      !confirm(
        direct > 0
          ? `Cancel ${po.number}? Its ${direct.toLocaleString("en-US")} units for backers go back to planning: another open make PO on the run takes them, else they show as still to build.`
          : `Cancel ${po.number}?`,
      )
    )
      return;
    mutateWithResult("po.set_status", { id: po.id, status }, [{ t: "po", id: po.id, row: { ...po, status } }]);
  };
  const remove = async () => {
    if (!confirm(`Delete draft ${po.number}${lineCount ? ` and its ${lineCount} lines` : ""}?`)) return;
    if (await mutate("po.remove", { id: po.id })) close();
  };

  return (
    <div className="flex items-start gap-3 border-b border-line px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[15px] font-semibold">{po.number}</span>
          <PaneLink
            kind="vendor"
            id={po.vendor_id}
            to={`/vendors?open=${po.vendor_id}`}
            className="truncate text-[15px] font-semibold hover:underline"
            title={vendorName}
          >
            {vendorShort}
          </PaneLink>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          <Badge tone={STATUS_TONE[po.status]}>{PO_STATUS_LABELS[po.status]}</Badge>
          {po.kind !== "purchase" && <Badge>{PO_KIND_LABELS[po.kind]}</Badge>}
          <IncomingBadge po={po} />
          {po.sent_revision !== null && <Badge tone={unsent ? "warn" : "neutral"}>{unsent ? `Rev ${po.revision} not sent yet` : `Rev ${po.revision}`}</Badge>}
          {daysLate(po) > 0 && <Badge tone="danger">{plural(daysLate(po), "day")} late</Badge>}
          {/* Cover through --- the day it was drafted to last to (its check plans with it) */}
          {po.cover_through && po.status === "draft" && (
            <span title="Drafted to last to then: its check against the plan covers to then">
              <Badge tone="accent">Covers to {coverDay(po.cover_through, planToday())}</Badge>
            </span>
          )}
          <span className="ml-1 text-[12px] text-faint">
            {po.sent_at
              ? `Sent ${ago(po.sent_at)} by ${who(po.sent_by)}${po.sent_revision ? ` (rev ${po.sent_revision})` : ""}`
              : `Created ${ago(po.created_at)}${po.created_by ? ` by ${who(po.created_by)}` : ""}`}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <SendToVendorButton
          po={po}
          vendor={vendor}
          primary={gmailOn && canSend}
          check={check}
          getCheck={getCheck}
          review={review}
          gaps={gaps}
          labelGaps={labelGaps}
        />
        {canSend && (
          <Button
            variant={gmailOn ? "secondary" : "primary"}
            size="sm"
            icon={<Send className="size-3.5" />}
            title={
              gmailOn
                ? "Record that the vendor has this PO without emailing it from here"
                : "Record that the vendor has this PO (email it from Gmail with the PDF for now)"
            }
            onClick={() => {
              const c = getCheck();
              const plan = c && needsWarning(c, po.plan_check_key) ? c : null;
              if (plan || gaps.length || labelGaps.length) setConfirming({ check: plan });
              else {
                setConfirming(null);
                markSent();
              }
            }}
          >
            {po.status === "draft" ? "Mark as sent" : `Mark rev ${po.revision} as sent`}
          </Button>
        )}
        <Button
          size="sm"
          icon={<FileText className="size-3.5" />}
          onClick={() => window.open(`/api/po/${po.id}/pdf?tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`, "_blank")}
        >
          PDF
        </Button>
        {/* Labels and printouts --- one per line */}
        <PrintMenu label="Labels" items={poLabels(po.id)} />
        {can("po.set_status") && next.length > 0 && (
          <Select
            aria-label="Move to"
            value=""
            options={[{ value: "", label: "Move to…" }, ...next.map((s) => ({ value: s, label: PO_STATUS_LABELS[s] }))]}
            onChange={(e) => e.target.value && move(e.target.value as PoStatus)}
            className="h-7 w-36 text-[12px]"
          />
        )}
        {can("po.merge") && <MergeDrafts po={po} />}
        {can("po.remove") && po.status === "draft" && po.sent_revision === null && (
          <button type="button" className="rounded p-1 text-faint hover:bg-hover hover:text-danger" title="Delete this draft" onClick={remove}>
            <Trash2 className="size-4" />
          </button>
        )}
        {!inPane && (
          <button type="button" onClick={close} className="rounded p-1 text-muted hover:bg-hover hover:text-text" title="Close (Esc)">
            <X className="size-4" />
          </button>
        )}
      </div>
      {acking && <AcknowledgeDialog po={po} onClose={() => setAcking(false)} />}
      {shipping && <ShippedDialog po={po} onClose={() => setShipping(false)} />}
      {confirming && confirmShown && (
        <SendConfirm
          check={confirming.check}
          gaps={gaps}
          labels={labelGaps}
          sendLabel={po.status === "draft" ? "Mark as sent anyway" : `Mark rev ${po.revision} as sent anyway`}
          onClose={() => setConfirming(null)}
          onReview={() => {
            setConfirming(null);
            review();
          }}
          onSend={() => {
            setConfirming(null);
            // The plan's override is recorded only when it warned; print-file gaps and label warnings aren't recorded.
            markSent(confirming.check ? sendAnyway(confirming.check) : undefined);
          }}
        />
      )}
    </div>
  );
}

/**
 * "Rev C of GCU2-MAIN was released after this PO was sent", with Send update while the lines can change. Packaging --- a
 * print-file gap that a newer revision fixes points to its button (data-send-update, packaging.ts).
 */
function NewerSpecs({ po, lines, specs }: { po: Po; lines: PoLine[]; specs: Map<string, LineSpec> }) {
  const newer = lines.flatMap((l) => {
    const s = specs.get(l.id);
    // Make POs --- and of a build's parts: "Rev B of SUB-PGC3-PCBA (in NSP3000)".
    return [
      ...(s?.newer ? [`Rev ${s.newer.rev} of ${l.sku || l.name}`] : []),
      ...(s?.parts ?? []).flatMap((p) => (p.newer ? [`Rev ${p.newer.rev} of ${p.sku || p.name} (in ${l.sku || l.name})`] : [])),
    ];
  });
  if (!newer.length) return null;
  const unique = [...new Set(newer)];
  const named = unique.length > 3 ? `${unique.slice(0, 3).join(", ")} and ${unique.length - 3} more` : unique.join(" and ");
  const canUpdate = can("po.send_spec_update") && linesEditable(po.status);
  const update = async () => {
    const { ok, result } = await mutateWithResult("po.send_spec_update", { id: po.id });
    if (ok) {
      const rev = (result as { revision: number }).revision;
      toast.ok(`Lines moved to the new revisions in rev ${rev}. Mark rev ${rev} as sent to show them on the vendor's documents page.`);
    }
  };
  return (
    <div className="flex items-center gap-3 border-b border-warn/30 bg-warn-soft px-4 py-2 text-[13px]">
      <RefreshCw className="size-4 shrink-0 text-warn" />
      <span className="min-w-0 flex-1">
        {named} {unique.length === 1 ? "was" : "were"} released after this PO was sent.{" "}
        <span className="text-muted">
          {canUpdate ? "The vendor still sees the revisions it was sent with." : "The goods have shipped, so the lines keep the revisions they were sent with."}
        </span>
      </span>
      {canUpdate && (
        <Button
          size="sm"
          variant="primary"
          icon={<Send className="size-3.5" />}
          onClick={update}
          data-send-update
          title="Moves these lines to the new revisions in the next PO revision; the vendor's documents page shows them once that revision is marked as sent"
        >
          Send update
        </Button>
      )}
    </div>
  );
}

const left: FieldDef[] = [
  { key: "payment_terms", label: "Payment terms", placeholder: "e.g. 30% deposit, 70% before shipment" },
  { key: "incoterms", label: "Incoterms", placeholder: "e.g. EXW, FOB Shenzhen" },
  { key: "deposit_pct", label: "Deposit %", type: "number", format: (v) => (v ? `${v}%` : "None") },
  { key: "currency", label: "Currency", mono: true },
  { key: "kind", label: "Kind", type: "select", options: PO_KINDS.map((k) => ({ value: k, label: PO_KIND_LABELS[k] })) },
];
const right: FieldDef[] = [
  { key: "expected_ship", label: "Ships by", type: "date" },
  { key: "expected_arrive", label: "Needed by", type: "date", placeholder: "Printed as the required date" },
  { key: "vendor_ref", label: "Their ref", placeholder: "PI or order confirmation number" },
  { key: "notes", label: "Notes on PO", type: "textarea", placeholder: "Printed: packaging, labelling, shipping marks" },
  { key: "internal_notes", label: "Internal notes", type: "textarea", placeholder: "Not printed" },
];

const SHIPPED = new Set<Po["status"]>(["shipped", "received", "closed"]);
const tracking: FieldDef[] = [
  { key: "carrier", label: "Carrier", placeholder: "e.g. DHL" },
  { key: "tracking_number", label: "Tracking", mono: true, placeholder: "Not printed" },
  { key: "shipped_on", label: "Shipped on", type: "date" },
];

function Terms({ po, disabled }: { po: Po; disabled: boolean }) {
  // An emptied deposit means none.
  const deposit = (value: unknown) => mutate("po.update", { id: po.id, patch: { deposit_pct: value == null ? 0 : Number(value) } });
  return (
    <div className="grid grid-cols-2 gap-x-4">
      <div className="flex min-w-0 flex-col gap-0.5">
        <ShipTo po={po} disabled={disabled} />
        <Fields
          compact
          row={po}
          // Consignment --- a make PO stays one (its kind isn't a choice)
          fields={left.filter((f) => po.kind !== "make" || f.key !== "kind").map((f) => (f.key === "deposit_pct" ? { ...f, save: deposit } : f))}
          update="po.update"
          disabled={disabled}
        />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <Fields compact row={po} fields={right} update="po.update" disabled={disabled} />
        {/* Inbound tracking --- once it's shipped (or someone filled it in); HHL's own, never printed. */}
        {(SHIPPED.has(po.status) || po.carrier || po.tracking_number || po.shipped_on) && (
          <>
            <Fields compact row={po} fields={tracking} update="po.update" disabled={disabled} />
            <TrackingText po={po} className="px-2 pb-1 text-[12px]" />
          </>
        )}
        <RunAndPaper po={po} />
      </div>
    </div>
  );
}

/**
 * Production runs --- the run this PO is on (one at most; changing it never touches what the vendor sees), and the dates
 * on paper for a PO recorded after the fact (its own date, sent, acknowledged), kept beside when it was recorded here.
 */
function RunAndPaper({ po }: { po: Po }) {
  const runs = useRows("production_run");
  const projects = useRows("project");
  const label = (r: (typeof runs)[number]) => `${r.number}${r.name ? ` · ${r.name}` : ""} (${projects.find((x) => x.id === r.project_id)?.code ?? ""})`;
  const options = [
    { value: "", label: "None" },
    ...runs
      .filter((r) => r.status !== "closed" || r.id === po.production_run_id)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .map((r) => ({ value: r.id, label: label(r) })),
  ];
  const editable = can("po.link_run");
  const history = (key: "dated_on" | "sent_on" | "acknowledged_on" | "received_on") => (v: unknown) =>
    mutate("po.set_history", { id: po.id, [key]: (v as string) || null });
  return (
    <>
      <Fields
        compact
        row={po}
        disabled={!editable}
        fields={[
          {
            key: "production_run_id",
            label: "Production run",
            type: "select",
            nullable: true,
            options,
            save: (v) => {
              const to = (v as string) || null;
              const from = runs.find((r) => r.id === po.production_run_id);
              if (from && to && !confirm(`${po.number} is on ${from.number}. Move it?`)) return;
              mutate("po.link_run", { po_id: po.id, run_id: to, move: !!from && !!to });
            },
          },
          { key: "dated_on", label: "Dated (on paper)", type: "date", placeholder: "If it was made before HHL Ops", save: history("dated_on") },
          // How long it took (migration 078): ordered → received, for lead times
          { key: "sent_on", label: "Ordered on", type: "date", placeholder: "The day it went, if not sent from here", save: history("sent_on") },
          { key: "acknowledged_on", label: "Acknowledged (on paper)", type: "date", save: history("acknowledged_on") },
          { key: "received_on", label: "Received on", type: "date", placeholder: "The day it all arrived, if not received here", save: history("received_on") },
        ]}
      />
      <TookDays po={po} />
    </>
  );
}

/** How long it took, ordered → received (poDates()), and making that its vendor's lead time for these items. */
function TookDays({ po }: { po: Po }) {
  const receipts = useRows("receipt").filter((r) => r.po_id === po.id);
  const vendor = useRow("vendor", po.vendor_id);
  const lines = useRows("po_line").filter((l) => l.po_id === po.id);
  const links = useRows("vendor_item").filter((l) => l.vendor_id === po.vendor_id && lines.some((x) => x.item_id === l.item_id));
  const d = poDates(po, receipts);
  if (d.days == null) return null;
  const same = lines.length > 0 && lines.every((l) => links.find((k) => k.item_id === l.item_id)?.lead_time_days === d.days);
  const name = vendor ? vendorLabel(vendor) : "the vendor";
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 pb-1 text-[12px]">
      <span>
        Took <span className="font-medium">{plural(d.days, "day")}</span>
        <span className="text-muted">
          {" "}
          (ordered {shortDate(d.ordered)}, received {shortDate(d.received)})
        </span>
      </span>
      {same ? (
        <span className="text-faint">{name}'s lead time for these</span>
      ) : (
        can("po.use_lead_time") && (
          <Button
            size="sm"
            variant="ghost"
            title={`Set ${name}'s lead time for ${plural(lines.length, "item")} on this PO to ${d.days} days, so planning orders them that far ahead`}
            onClick={async () => {
              const { ok, result } = await mutateWithResult("po.use_lead_time", { po_id: po.id, days: d.days as number });
              const r = (result ?? {}) as { updated?: number; made?: number };
              if (ok) toast.ok(`${name}'s lead time is ${d.days} days for ${plural((r.updated ?? 0) + (r.made ?? 0), "item")}`);
            }}
          >
            Use as {name}'s lead time
          </Button>
        )
      )}
    </div>
  );
}

type Entry = {
  id: number;
  at: string;
  via: string;
  action: string;
  entity: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  user_name: string | null;
  user_email: string | null;
  line_sku: string | null;
};

const HIDDEN = new Set([
  "id",
  "po_id",
  "item_id",
  "created_at",
  "updated_at",
  "photo_url",
  "position",
  "qty_shipped",
  "qty_received",
  "qty_rejected",
  "plan_basis", // Check against the plan --- the snapshot and the fingerprint aren't for reading
  "plan_check_key",
]);
// Shopify bookkeeping (the Receipts tab shows it) and the Gmail thread ids (the email strip); receipts do show what they received.
const SHOPIFY_KEYS = /^(shopify|gmail)_/;
const RECEIVED = new Set(["qty_received", "qty_rejected"]);
const ISO = /^\d{4}-\d{2}-\d{2}T[\d:.]+Z$/;
/** A changed value as people read it: dates without midnight times, user ids as names, spec revisions as "Rev C". */
const shown = (key: string, v: unknown, userName: (id: string) => string, revName: (id: string) => string) => {
  if (v == null || v === "") return "empty";
  if (typeof v === "string" && key === "spec_revision_id") return revName(v);
  if (typeof v === "string" && key.endsWith("_by")) return userName(v);
  if (typeof v === "string" && ISO.test(v)) return v.includes("T00:00:00") ? v.slice(0, 10) : new Date(v).toLocaleString();
  return typeof v === "object" ? JSON.stringify(v) : String(v);
};

function describe(e: Entry) {
  const line = e.line_sku || "a line";
  if (e.entity === "doc_link")
    return !e.before ? "made the documents link" : e.after?.revoked_at ? "turned off the documents link" : "changed the documents link";
  if (e.action === "po.send_spec_update" && e.entity === "po_line") return `sent the spec update for ${line}`;
  // Check against the plan ---
  if (e.action === "po.update_from_plan" && e.entity === "po_line")
    return !e.before ? `added ${line} from the plan` : !e.after ? `took ${line} off (the plan doesn't need it now)` : `set ${line} to the plan's quantity`;
  if (e.action === "po.update_from_plan" && e.entity === "po" && e.after?.plan_checked_at) return "updated it from the plan";
  if (e.action === "po.plan_checked") return "checked it against the plan and kept it as it is";
  // Its starting point, recorded as it was made (server/src/po-plan-check.ts recordDraftStart()).
  if ((e.action === "po.create" || e.action === "po.create_make") && e.entity === "po" && e.before && e.after?.plan_checked_at)
    return "checked it against the plan as it was drafted";
  if (e.after?.plan_check_overridden) return "sent it although it differed from the plan";
  if (e.entity === "po_line") return !e.before ? `added ${line}` : !e.after ? `removed ${line}` : `changed ${line}`;
  if (e.action === "receipt.post") return e.entity === "po_line" ? `received ${line}` : "received a delivery";
  if (!e.before) return "created this PO";
  if (e.action === "po.send_email") return e.after?.status === "sent" || e.after?.sent_revision !== undefined ? "emailed it to the vendor" : "changed"; // PO email ---
  if (e.action === "po.acknowledge") return "recorded the vendor's confirmation";
  if (e.action === "po.mark_sent" || (e.action === "po.set_status" && e.after?.status === "sent")) return "marked it as sent";
  return "changed";
}

/** The PO's changes and its lines', newest first. */
function PoHistory({ id }: { id: string }) {
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const users = useRows("app_user");
  const userName = (uid: string) => personName(users, uid);
  const revisions = useRows("spec_revision");
  const revName = (rid: string) => `Rev ${revisions.find((r) => r.id === rid)?.rev ?? "?"}`;
  useEffect(() => {
    let live = true;
    fetch(`/api/po/${id}/history`)
      .then((r) => r.json())
      .then((rows: Entry[]) => live && setEntries(rows));
    return () => {
      live = false;
    };
  }, [id]);
  if (!entries) return null;
  return (
    <ol className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
      {entries.map((e) => {
        const keys = Object.keys(e.after ?? {}).filter((k) => (!HIDDEN.has(k) || (e.action === "receipt.post" && RECEIVED.has(k))) && !SHOPIFY_KEYS.test(k));
        if (e.before && e.after && !keys.length) return null;
        return (
          <li key={e.id} className="flex gap-2">
            <History className="mt-0.5 size-3.5 shrink-0 text-faint" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-medium">{e.user_name || e.user_email || "HHL Ops"}</span>
                <span className="text-muted">{describe(e)}</span>
                {e.via !== "ui" && <Badge>{e.via}</Badge>}
                <span className="ml-auto text-[11px] text-faint">{new Date(e.at).toLocaleString()}</span>
              </div>
              {e.before && e.after && (
                <div className="mt-1 flex flex-col gap-0.5 text-[12px]">
                  {keys.map((k) => (
                    <div key={k} className="truncate">
                      <span className="text-muted">{k}: </span>
                      <span className="text-faint line-through">{shown(k, e.before?.[k], userName, revName)}</span> →{" "}
                      <span>{shown(k, e.after?.[k], userName, revName)}</span>
                    </div>
                  ))}
                </div>
              )}
              {!e.before && e.entity === "po_line" && e.after && (
                <div className="mt-1 text-[12px] text-muted">
                  {qty(e.after.qty)} × {e.after.unit_price == null ? "no price" : money(e.after.unit_price)}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Merge drafts (Mitch, 2026-11-03): this draft's lines onto another draft to the same vendor and ship-to (an item on
 * both is one line with both quantities), and this one goes (po.merge). Shown only when there's another to merge into.
 */
function MergeDrafts({ po }: { po: Po }) {
  const pos = useRows("po");
  const lines = useRows("po_line");
  const mergeable = (p: Po) => p.status === "draft" && p.kind === "purchase" && p.sent_revision === null;
  if (!mergeable(po)) return null;
  const others = pos
    .filter((p) => p.id !== po.id && p.vendor_id === po.vendor_id && mergeable(p) && (p.ship_to_location_id ?? null) === (po.ship_to_location_id ?? null))
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  if (!others.length) return null;
  const count = (id: string) => lines.filter((l) => l.po_id === id).length;
  const merge = async (into: Po) => {
    const { ok } = await mutateWithResult("po.merge", { from_id: po.id, into_id: into.id });
    if (!ok) return;
    toast.ok(`${po.number} merged into ${into.number}`);
    location.pathname === "/pos" ? setSearch({ open: into.id, pane: null }) : navigate(poPath(into.id));
  };
  return (
    <Popover
      width={320}
      button={(toggle) => (
        <Button
          size="sm"
          variant="ghost"
          icon={<Merge className="size-3.5" />}
          onClick={toggle}
          title="Put this draft's lines on another draft to the same vendor"
        >
          Merge…
        </Button>
      )}
    >
      {(close) => (
        <div className="flex flex-col gap-1">
          <div className="pb-1 font-medium">Merge {po.number} into…</div>
          <div className="pb-1 text-[12px] text-muted">
            Its {count(po.id)} line{count(po.id) === 1 ? "" : "s"} go onto the draft you pick (an item on both becomes one line with both quantities), and{" "}
            {po.number} is deleted.
          </div>
          {others.map((o) => (
            <button
              key={o.id}
              type="button"
              className="flex items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-hover"
              onClick={() => {
                close();
                void merge(o);
              }}
            >
              <span className="font-mono text-[12px] font-medium">{o.number}</span>
              <span className="text-[12px] text-muted">
                {count(o.id)} line{count(o.id) === 1 ? "" : "s"} · made {ago(o.created_at)}
              </span>
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}
